import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Workflow, Target, TrendingUp, Users2, Briefcase, HeartHandshake, FileCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";
import { Seo } from "@/lib/seo";
import { SectionHeading, Reveal, ProcessTimeline, CTASection, FAQSection, CheckItem } from "@/components/shared/Shared";
import { useContent } from "@/context/ContentContext";

const IMG = {
  team: "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1400&auto=format&fit=crop",
};

export default function Karriere() {
  const { t } = useLang();
  const hero = useContent("karriere.hero");
  const faqContent = useContent("faq.karriere");

  const audiences = [
    { icon: Users2, label: t("Quereinsteiger", "Career changers") },
    { icon: Briefcase, label: t("Erfahrene Finanzberater", "Experienced financial advisors") },
    { icon: TrendingUp, label: t("Vertriebspers\u00f6nlichkeiten", "Sales personalities") },
    { icon: HeartHandshake, label: t("Unternehmer & Partner", "Entrepreneurs & partners") },
  ];

  const expectations = [
    { icon: HeartHandshake, title: t("Pers\u00f6nliche Beratung", "Personal advice"), text: t("Sie beraten Mandanten pers\u00f6nlich und langfristig \u2013 ohne Produktdruck und ohne Strukturvertriebs-Mechanik.", "You advise clients personally and long-term \u2013 without product pressure and without structured sales mechanics.") },
    { icon: FileCheck2, title: t("Strukturierte Prozesse", "Structured processes"), text: t("Klare Abl\u00e4ufe von der Anfrage bis zur Dokumentation \u2013 damit Sie sich auf Beratung konzentrieren k\u00f6nnen.", "Clear workflows from inquiry to documentation \u2013 so you can focus on advising.") },
    { icon: GraduationCap, title: t("Schulung & Einarbeitung", "Training & onboarding"), text: t("Strukturierte Einarbeitung mit Schulungsmodulen, Wissenstests und pers\u00f6nlichem Mentor.", "Structured onboarding with training modules, knowledge tests and a personal mentor.") },
    { icon: Workflow, title: t("Digitale Unterst\u00fctzung", "Digital support"), text: t("Moderne digitale Werkzeuge f\u00fcr Anfragen, Termine und Dokumentation \u2013 statt Zettelwirtschaft.", "Modern digital tools for inquiries, appointments and documentation \u2013 instead of paperwork.") },
    { icon: Target, title: t("Klare Leistungsbereiche", "Clear service areas"), text: t("Family Office, Real Estate, Maklerservice und Development \u2013 fokussiert statt beliebig.", "Family Office, Real Estate, Brokerage and Development \u2013 focused instead of arbitrary.") },
    { icon: TrendingUp, title: t("Karrierepfade", "Career paths"), text: t("Definierte Entwicklungsstufen mit klaren Meilensteinen \u2013 vom Einstieg bis zur Teamverantwortung.", "Defined development levels with clear milestones \u2013 from entry to team responsibility.") },
  ];

  const support = [
    t("Eleven Academy mit Schulungsmodulen und Zertifikaten", "Eleven Academy with training modules and certificates"),
    t("CRM-gest\u00fctzte Prozesse und Leadmanagement", "CRM-supported processes and lead management"),
    t("Strukturierte Dokumentations- und Compliance-Prozesse", "Structured documentation and compliance processes"),
    t("Pers\u00f6nliches Onboarding mit Mentor", "Personal onboarding with a mentor"),
    t("Digitale Anfragewege und Terminbuchung", "Digital request channels and appointment booking"),
    t("Backoffice-Unterst\u00fctzung im Tagesgesch\u00e4ft", "Back office support in daily business"),
  ];

  const path = [
    { title: t("Einstieg & Einarbeitung", "Entry & onboarding"), text: t("Strukturierte Schulungen und begleitete Praxis \u2013 auch f\u00fcr Quereinsteiger.", "Structured training and supervised practice \u2013 also for career changers.") },
    { title: t("Berater", "Advisor"), text: t("Eigene Mandanten und eigenverantwortliche Beratung mit voller Unterst\u00fctzung.", "Your own clients and independent advising with full support.") },
    { title: t("Senior-Berater", "Senior Advisor"), text: t("Komplexe Mandate, Spezialisierung und Mentoring neuer Kollegen.", "Complex mandates, specialization and mentoring of new colleagues.") },
    { title: t("Teamverantwortung", "Team responsibility"), text: t("Aufbau und F\u00fchrung eines eigenen Beratungsbereichs.", "Building and leading your own advisory area.") },
  ];

  const faqDefaults = [
    { q: t("Ist Eleven Family Office ein Strukturvertrieb?", "Is Eleven Family Office a structured sales organization?"), a: t("Nein. Es gibt keine Downline-Verg\u00fctung und keine Rekrutierungspflicht. Entwicklung erfolgt \u00fcber Beratungsqualit\u00e4t und dokumentierte Leistung.", "No. There is no downline remuneration and no recruitment obligation. Development is based on advisory quality and documented performance.") },
    { q: t("Brauche ich Vorerfahrung?", "Do I need prior experience?"), a: t("Nicht zwingend. Die strukturierte Einarbeitung mit der Eleven Academy bereitet Quereinsteiger gezielt vor.", "Not necessarily. The structured onboarding with the Eleven Academy specifically prepares career changers.") },
    { q: t("Wie l\u00e4uft die Bewerbung ab?", "How does the application work?"), a: t("Nach Ihrer Bewerbung folgen ein Kennenlerngespr\u00e4ch, ein Fachgespr\u00e4ch und ein pers\u00f6nlicher Onboarding-Plan.", "After your application follow an introductory meeting, a technical interview and a personal onboarding plan.") },
  ];
  const faq = (faqContent.items || []).length ? faqContent.items : faqDefaults;

  return (
    <div>
      <Seo
        title={t("Karriere & Partner \u2013 Moderne Finanzberatung", "Careers & Partners \u2013 Modern Financial Advisory")}
        description={t(
          "Karriere bei Eleven Family Office: strukturierte Einarbeitung, Schulungen, digitale Prozesse und klare Karrierepfade \u2013 f\u00fcr Quereinsteiger und erfahrene Berater. Jetzt bewerben.",
          "Careers at Eleven Family Office: structured onboarding, training, digital processes and clear career paths \u2013 for career changers and experienced advisors. Apply now."
        )}
      />
      {/* HERO */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <SectionHeading
                eyebrow={hero.eyebrow || t("Karriere & Partner", "Careers & Partners")}
                title={hero.headline || t("Karriere in der modernen Finanzberatung.", "A career in modern financial advisory.")}
                subtitle={hero.subline || t(
                  "Eleven Family Office bietet Beratern und Partnern eine professionelle Umgebung f\u00fcr pers\u00f6nliche Beratung, digitale Prozesse und langfristige Mandantenbetreuung.",
                  "Eleven Family Office offers advisors and partners a professional environment for personal advice, digital processes and long-term client support."
                )}
              />
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-[6px] h-12 px-6 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold" data-testid="berater-bewerben-cta">
                  <Link to="/anfrage/beraterbewerbung">{t("Jetzt bewerben", "Apply now")} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-[6px] h-12 px-6 hover:border-[#FF3C20]/60" data-testid="partner-werden-cta">
                  <Link to="/anfrage/partner">{t("Partner werden", "Become a partner")}</Link>
                </Button>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-3" data-testid="karriere-audiences">
                {audiences.map((a) => (
                  <div key={a.label} className="flex items-center gap-2.5 rounded-[6px] border border-border bg-[#F6F7F8] px-3.5 py-3">
                    <a.icon className="h-4 w-4 text-[#E8241B] shrink-0" />
                    <span className="text-xs font-medium text-[#2A303A]">{a.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <Reveal>
              <div className="relative rounded-[8px] overflow-hidden ring-1 ring-black/5">
                <img src={IMG.team} alt={t("Team bei Eleven Family Office", "Team at Eleven Family Office")} className="w-full h-[440px] object-cover" loading="lazy" decoding="async" width="1400" height="875" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT TO EXPECT */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow={t("Was Sie erwartet", "What to expect")} title={t("Beratung, wie sie sein sollte", "Advisory the way it should be")} />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expectations.map((b) => (
              <Reveal key={b.title} className="rounded-[8px] bg-white border border-border p-8 card-hover">
                <div className="h-11 w-11 rounded-[6px] bg-[#F1F2F4] border border-border flex items-center justify-center">
                  <b.icon className="h-5 w-5 text-[#E8241B]" />
                </div>
                <h4 className="mt-5 text-lg font-semibold text-[#111318]">{b.title}</h4>
                <p className="mt-2.5 text-sm text-[#3B4452] leading-relaxed">{b.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SUPPORT BY ELEVEN */}
      <section className="dark-section noise-overlay py-14 sm:py-20" data-testid="karriere-support-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <SectionHeading
              dark
              eyebrow={t("Unterst\u00fctzung durch Eleven Family Office", "Support by Eleven Family Office")}
              title={t("Sie beraten. Wir halten Ihnen den R\u00fccken frei.", "You advise. We take care of the rest.")}
              subtitle={t(
                "Hinter jeder Beratung steht ein professionelles System: Schulung, Prozesse und digitale Werkzeuge \u2013 als Unterst\u00fctzung f\u00fcr Sie, nicht als Selbstzweck.",
                "Behind every consultation stands a professional system: training, processes and digital tools \u2013 as support for you, not as an end in itself."
              )}
            />
            <div className="space-y-3">
              {support.map((s) => (
                <Reveal key={s} className="panel p-4">
                  <CheckItem dark>{s}</CheckItem>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CAREER PATH */}
      <section className="bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow={t("Karrierepfade", "Career paths")} title={t("Definierte Entwicklung statt leerer Titel", "Defined development instead of empty titles")} />
          <div className="mt-12"><ProcessTimeline steps={path} /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F1F2F4] py-14 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="FAQ" title={t("H\u00e4ufige Fragen von Bewerbern", "Frequently asked applicant questions")} />
          <div className="mt-10"><FAQSection items={faq} /></div>
        </div>
      </section>

      <CTASection
        title={t("Bereit f\u00fcr den n\u00e4chsten Schritt?", "Ready for the next step?")}
        subtitle={t("Bewerben Sie sich als Berater oder starten Sie eine Partnerschaft \u2013 wir melden uns pers\u00f6nlich bei Ihnen.", "Apply as an advisor or start a partnership \u2013 we will contact you personally.")}
        primaryLabel={t("Jetzt bewerben", "Apply now")}
        primaryTo="/anfrage/beraterbewerbung"
        secondaryLabel={t("Partner werden", "Become a partner")}
        secondaryTo="/anfrage/partner"
      />
    </div>
  );
}
